import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';

// Format currency for PDF
const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value || 0);
};

// Format date DD/MM/YYYY
const formatDate = (date) => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Add header to PDF
const addHeader = (doc, title, subtitle = '') => {
  // Logo area
  doc.setFillColor(190, 18, 60); // Primary red color
  doc.rect(0, 0, 220, 25, 'F');
  
  // Title
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('FinScope', 14, 15);
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Inteligencia Financiera', 14, 21);
  
  // Report title
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 14, 38);
  
  if (subtitle) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(subtitle, 14, 45);
  }
  
  // Date
  doc.setFontSize(9);
  doc.setTextColor(150, 150, 150);
  doc.text(`Generado: ${formatDate(new Date())}`, 14, subtitle ? 52 : 45);
  
  return subtitle ? 60 : 53;
};

// Add footer to PDF
const addFooter = (doc, pageNumber, totalPages) => {
  const pageHeight = doc.internal.pageSize.height;
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.text(`Página ${pageNumber} de ${totalPages}`, 14, pageHeight - 10);
  doc.text('FinScope - Reporte Confidencial', doc.internal.pageSize.width - 60, pageHeight - 10);
};

// Export Dashboard to PDF
export const exportDashboardToPDF = async (kpis, tendencias, gastosPorCategoria, periodo = '') => {
  const doc = new jsPDF();
  let yPos = addHeader(doc, 'Dashboard Financiero', periodo);
  
  // KPIs Section
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Indicadores Clave', 14, yPos);
  yPos += 8;
  
  // KPIs table
  const kpiData = [
    ['Ingresos', formatCurrency(kpis?.ingresos || 0), `${kpis?.ingresos_cambio > 0 ? '+' : ''}${kpis?.ingresos_cambio || 0}%`],
    ['Egresos', formatCurrency(kpis?.egresos || 0), `${kpis?.egresos_cambio > 0 ? '+' : ''}${kpis?.egresos_cambio || 0}%`],
    ['Utilidad', formatCurrency(kpis?.utilidad || 0), ''],
    ['Margen', `${kpis?.margen || 0}%`, ''],
    ['YTD', formatCurrency(kpis?.ytd || 0), '']
  ];
  
  autoTable(doc, {
    startY: yPos,
    head: [['Métrica', 'Valor', 'vs Mes Anterior']],
    body: kpiData,
    theme: 'striped',
    headStyles: { fillColor: [190, 18, 60] },
    margin: { left: 14, right: 14 },
    styles: { fontSize: 10 }
  });
  
  yPos = doc.lastAutoTable.finalY + 15;
  
  // Gastos por Categoría
  if (gastosPorCategoria && gastosPorCategoria.length > 0) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Gastos por Categoría', 14, yPos);
    yPos += 8;
    
    // Calculate total for percentage
    const totalGastos = gastosPorCategoria.reduce((sum, cat) => sum + (cat.total || cat.monto || 0), 0);
    
    const categoriasData = gastosPorCategoria.map(cat => {
      const monto = cat.total || cat.monto || 0;
      const porcentaje = totalGastos > 0 ? ((monto / totalGastos) * 100).toFixed(1) : 0;
      return [
        cat.categoria_nombre || cat.nombre || cat.categoria || 'Sin categoría',
        formatCurrency(monto),
        `${porcentaje}%`
      ];
    });
    
    autoTable(doc, {
      startY: yPos,
      head: [['Categoría', 'Monto', '% del Total']],
      body: categoriasData,
      theme: 'striped',
      headStyles: { fillColor: [190, 18, 60] },
      margin: { left: 14, right: 14 },
      styles: { fontSize: 10 }
    });
    
    yPos = doc.lastAutoTable.finalY + 10;
    
    // Add total row
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`Total Gastos: ${formatCurrency(totalGastos)}`, 14, yPos);
  }
  
  addFooter(doc, 1, 1);
  doc.save('dashboard_financiero.pdf');
};

// Export Rentabilidad HAUS to PDF
export const exportRentabilidadHausToPDF = async (resumen, clientes, cuentas, colaboradores) => {
  const doc = new jsPDF();
  let yPos = addHeader(doc, 'Rentabilidad HAUS', 'Análisis de rentabilidad por cliente y cuenta');
  
  // Resumen KPIs
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Resumen Ejecutivo', 14, yPos);
  yPos += 8;
  
  const resumenData = [
    ['Ingresos Totales', formatCurrency(resumen?.total_ingresos)],
    ['Costos Totales', formatCurrency(resumen?.total_costos)],
    ['Utilidad Total', formatCurrency(resumen?.utilidad_total)],
    ['Margen Promedio', `${resumen?.margen_promedio || 0}%`],
    ['Total Clientes', resumen?.total_clientes || 0],
    ['Clientes Rentables', resumen?.clientes_rentables || 0],
    ['Clientes No Rentables', resumen?.clientes_no_rentables || 0]
  ];
  
  autoTable(doc, {
    startY: yPos,
    head: [['Indicador', 'Valor']],
    body: resumenData,
    theme: 'striped',
    headStyles: { fillColor: [190, 18, 60] },
    margin: { left: 14, right: 100 },
    styles: { fontSize: 10 },
    columnStyles: { 0: { cellWidth: 50 }, 1: { cellWidth: 40 } }
  });
  
  yPos = doc.lastAutoTable.finalY + 15;
  
  // Rentabilidad por Cuenta
  if (cuentas && cuentas.length > 0) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Rentabilidad por Cuenta', 14, yPos);
    yPos += 8;
    
    const cuentasData = cuentas.map(c => [
      c.cuenta_nombre,
      c.num_clientes || 0,
      formatCurrency(c.ingreso_total),
      formatCurrency(c.costo_total),
      formatCurrency(c.utilidad),
      `${c.margen || 0}%`
    ]);
    
    autoTable(doc, {
      startY: yPos,
      head: [['Cuenta', '# Clientes', 'Ingreso', 'Costo', 'Utilidad', 'Margen']],
      body: cuentasData,
      theme: 'striped',
      headStyles: { fillColor: [190, 18, 60] },
      margin: { left: 14, right: 14 },
      styles: { fontSize: 9 }
    });
    
    yPos = doc.lastAutoTable.finalY + 15;
  }
  
  // Check if we need a new page
  if (yPos > 200) {
    doc.addPage();
    yPos = 20;
  }
  
  // Rentabilidad por Cliente
  if (clientes && clientes.length > 0) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Rentabilidad por Cliente', 14, yPos);
    yPos += 8;
    
    const clientesData = clientes.map(c => [
      c.nombre_cliente,
      c.cuenta_nombre,
      formatCurrency(c.ingreso_total),
      formatCurrency(c.costo_total),
      formatCurrency(c.utilidad),
      `${c.margen || 0}%`
    ]);
    
    autoTable(doc, {
      startY: yPos,
      head: [['Cliente', 'Cuenta', 'Ingreso', 'Costo', 'Utilidad', 'Margen']],
      body: clientesData,
      theme: 'striped',
      headStyles: { fillColor: [190, 18, 60] },
      margin: { left: 14, right: 14 },
      styles: { fontSize: 9 }
    });
    
    yPos = doc.lastAutoTable.finalY + 15;
  }
  
  // Check if we need a new page for colaboradores
  if (yPos > 200 && colaboradores && colaboradores.length > 0) {
    doc.addPage();
    yPos = 20;
  }
  
  // Rentabilidad por Colaborador
  if (colaboradores && colaboradores.length > 0) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Rentabilidad por Colaborador', 14, yPos);
    yPos += 8;
    
    const colabData = colaboradores.map(c => [
      c.nombre,
      c.cargo,
      c.num_clientes || 0,
      `${c.porcentaje_carga || 0}%`,
      formatCurrency(c.costo_imputado),
      formatCurrency(c.ingreso_asociado),
      formatCurrency(c.rentabilidad_generada)
    ]);
    
    autoTable(doc, {
      startY: yPos,
      head: [['Colaborador', 'Cargo', 'Clientes', '% Carga', 'Costo', 'Ingreso', 'Rentabilidad']],
      body: colabData,
      theme: 'striped',
      headStyles: { fillColor: [190, 18, 60] },
      margin: { left: 14, right: 14 },
      styles: { fontSize: 8 }
    });
  }
  
  // Add page numbers
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    addFooter(doc, i, pageCount);
  }
  
  doc.save('rentabilidad_haus.pdf');
};

// Export Pagos to PDF
export const exportPagosToPDF = async (pagos, totales = {}) => {
  const doc = new jsPDF();
  let yPos = addHeader(doc, 'Reporte de Pagos Pendientes', `Total: ${pagos?.length || 0} pagos`);
  
  // Totales Section
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Resumen', 14, yPos);
  yPos += 8;
  
  const totalesData = [
    ['Total Pendiente', formatCurrency(totales?.pendiente || 0)],
    ['Total Programado', formatCurrency(totales?.programado || 0)],
    ['Total Vencido', formatCurrency(totales?.vencido || 0)],
    ['Total General', formatCurrency(totales?.total || 0)]
  ];
  
  autoTable(doc, {
    startY: yPos,
    head: [['Estado', 'Monto']],
    body: totalesData,
    theme: 'striped',
    headStyles: { fillColor: [190, 18, 60] },
    margin: { left: 14, right: 120 },
    styles: { fontSize: 10 }
  });
  
  yPos = doc.lastAutoTable.finalY + 15;
  
  // Pagos List
  if (pagos && pagos.length > 0) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Detalle de Pagos', 14, yPos);
    yPos += 8;
    
    const pagosData = pagos.map(p => [
      p.proveedor,
      p.concepto?.substring(0, 25) + (p.concepto?.length > 25 ? '...' : ''),
      formatCurrency(p.monto),
      p.fecha_vencimiento,
      p.estatus?.toUpperCase(),
      p.prioridad
    ]);
    
    autoTable(doc, {
      startY: yPos,
      head: [['Proveedor', 'Concepto', 'Monto', 'Vencimiento', 'Estado', 'Prioridad']],
      body: pagosData,
      theme: 'striped',
      headStyles: { fillColor: [190, 18, 60] },
      margin: { left: 14, right: 14 },
      styles: { fontSize: 8 },
      didParseCell: function(data) {
        if (data.column.index === 4 && data.cell.raw === 'VENCIDO') {
          data.cell.styles.textColor = [239, 68, 68];
          data.cell.styles.fontStyle = 'bold';
        }
      }
    });
  }
  
  // Add page numbers
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    addFooter(doc, i, pageCount);
  }
  
  doc.save('pagos_pendientes.pdf');
};

// Export Flujo de Caja to PDF
export const exportFlujoCajaToPDF = async (kpis, flujoMensual) => {
  const doc = new jsPDF();
  let yPos = addHeader(doc, 'Flujo de Caja', 'Análisis de flujo de efectivo');
  
  // KPIs
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Indicadores de Flujo', 14, yPos);
  yPos += 8;
  
  const kpiData = [
    ['Total Ingresos', formatCurrency(kpis?.total_ingresos)],
    ['Total Egresos', formatCurrency(kpis?.total_egresos)],
    ['Flujo Neto', formatCurrency(kpis?.flujo_neto)],
    ['Promedio Mensual', formatCurrency(kpis?.promedio_mensual)]
  ];
  
  autoTable(doc, {
    startY: yPos,
    head: [['Indicador', 'Valor']],
    body: kpiData,
    theme: 'striped',
    headStyles: { fillColor: [190, 18, 60] },
    margin: { left: 14, right: 100 },
    styles: { fontSize: 10 }
  });
  
  yPos = doc.lastAutoTable.finalY + 15;
  
  // Flujo Mensual
  if (flujoMensual && flujoMensual.length > 0) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Detalle Mensual', 14, yPos);
    yPos += 8;
    
    const flujoData = flujoMensual.map(f => [
      f.mes,
      formatCurrency(f.ingresos),
      formatCurrency(f.egresos),
      formatCurrency(f.flujo_neto),
      formatCurrency(f.saldo_acumulado)
    ]);
    
    autoTable(doc, {
      startY: yPos,
      head: [['Mes', 'Ingresos', 'Egresos', 'Flujo Neto', 'Saldo Acumulado']],
      body: flujoData,
      theme: 'striped',
      headStyles: { fillColor: [190, 18, 60] },
      margin: { left: 14, right: 14 },
      styles: { fontSize: 9 }
    });
  }
  
  addFooter(doc, 1, 1);
  doc.save('flujo_caja.pdf');
};

// Export P&L (Estado de Resultados) to PDF
export const exportPLToPDF = async (data, periodo = '') => {
  const doc = new jsPDF();
  let yPos = addHeader(doc, 'Estado de Resultados', periodo);
  
  // Ingresos Section
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('INGRESOS', 14, yPos);
  yPos += 8;
  
  if (data?.ingresos && data.ingresos.length > 0) {
    const ingresosData = data.ingresos.map(item => [
      item.label || item.concepto || item.categoria || 'Sin nombre',
      formatCurrency(item.amount || item.monto || 0)
    ]);
    ingresosData.push(['TOTAL INGRESOS', formatCurrency(data.total_ingresos || 0)]);
    
    autoTable(doc, {
      startY: yPos,
      body: ingresosData,
      theme: 'plain',
      margin: { left: 14, right: 14 },
      styles: { fontSize: 10 },
      columnStyles: {
        0: { cellWidth: 120 },
        1: { cellWidth: 50, halign: 'right' }
      },
      didParseCell: function(hookData) {
        if (hookData.row.index === ingresosData.length - 1) {
          hookData.cell.styles.fontStyle = 'bold';
          hookData.cell.styles.fillColor = [230, 255, 230];
        }
      }
    });
    yPos = doc.lastAutoTable.finalY + 10;
  } else {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Sin datos de ingresos', 14, yPos);
    yPos += 15;
  }
  
  // Egresos Section
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('EGRESOS', 14, yPos);
  yPos += 8;
  
  if (data?.egresos && data.egresos.length > 0) {
    const egresosData = data.egresos.map(item => [
      item.label || item.concepto || item.categoria || 'Sin clasificar',
      formatCurrency(item.amount || item.monto || 0)
    ]);
    egresosData.push(['TOTAL EGRESOS', formatCurrency(data.total_egresos || 0)]);
    
    autoTable(doc, {
      startY: yPos,
      body: egresosData,
      theme: 'plain',
      margin: { left: 14, right: 14 },
      styles: { fontSize: 10 },
      columnStyles: {
        0: { cellWidth: 120 },
        1: { cellWidth: 50, halign: 'right' }
      },
      didParseCell: function(hookData) {
        if (hookData.row.index === egresosData.length - 1) {
          hookData.cell.styles.fontStyle = 'bold';
          hookData.cell.styles.fillColor = [255, 230, 230];
        }
      }
    });
    yPos = doc.lastAutoTable.finalY + 15;
  } else {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Sin datos de egresos', 14, yPos);
    yPos += 15;
  }
  
  // Resultado
  doc.setFillColor(190, 18, 60);
  doc.rect(14, yPos, doc.internal.pageSize.width - 28, 12, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('UTILIDAD NETA', 18, yPos + 8);
  doc.text(formatCurrency(data?.utilidad_neta || 0), doc.internal.pageSize.width - 50, yPos + 8);
  
  // Margen
  yPos += 18;
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);
  doc.text(`Margen: ${data?.margen || 0}%`, 14, yPos);
  
  addFooter(doc, 1, 1);
  doc.save('estado_resultados.pdf');
};

// Capture element as image and add to PDF
export const captureElementToPDF = async (elementId, filename, title) => {
  const element = document.getElementById(elementId);
  if (!element) {
    console.error('Element not found:', elementId);
    return;
  }
  
  try {
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff'
    });
    
    const imgData = canvas.toDataURL('image/png');
    const doc = new jsPDF('l', 'mm', 'a4'); // Landscape
    
    addHeader(doc, title);
    
    const imgWidth = doc.internal.pageSize.width - 28;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    
    doc.addImage(imgData, 'PNG', 14, 50, imgWidth, imgHeight);
    
    addFooter(doc, 1, 1);
    doc.save(filename);
  } catch (error) {
    console.error('Error capturing element:', error);
  }
};

export default {
  exportDashboardToPDF,
  exportRentabilidadHausToPDF,
  exportPagosToPDF,
  exportFlujoCajaToPDF,
  exportPLToPDF,
  captureElementToPDF
};
